
import * as survey from "@aidkitorg/types/lib/survey";
import { DashboardSection, BooleanExpr, Queue } from "@aidkitorg/types/lib/survey";
import { DatabaseIcon, DownloadIcon } from "@heroicons/react/outline";
import { createContext, Dispatch, SetStateAction, useContext, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { usePost } from "./API";
import { Dropdown } from "./Components/Dropdown";
import { ThreeColumnPage } from "./Components/ThreeColumnPage";
import InterfaceContext, { PublicConfigurationContext } from "./Context";
import { BarChart } from "./DistroDashboard/BarChart";
import { HeroNumber } from "./DistroDashboard/HeroNumber";
import { PieChart } from "./DistroDashboard/PieChart";
import { copyToClipboard, RigidSpinner, SpacedSpinner } from "./Util";
import { Localizer, useLocalizedStrings, useLocalizer } from "./Localization";
import { toast } from "react-toastify";
import { encodeQueueForExplore } from "./Explore";
import { DashboardExplanationComponent } from "./DistroDashboard/ExplanationComponent";
import { DistroDashboardTable } from "./DistroDashboard/DistroDashboardTable";
import { DataDictionary } from "./DistroDashboard/DataDictionary";
import { Translations } from "@aidkitorg/i18n/lib";
import { PublicConfig } from "@aidkitorg/types/lib/config";

type DownloadTableFunction = (path: string, name: string) => Promise<void>;

type TableComponentProps = {
    component: survey.ApplicantTable | survey.Table,
    isLoading: boolean,
    setIsLoading: Dispatch<SetStateAction<boolean>>,
    downloadAtPath: DownloadTableFunction,
    downloadQualifier?: string,
    sqlButton: any,
    results?: any,
    L: Translations,
    localize: Localizer,
};

type DashboardComponentProps = {
    section: DashboardSection['components'][number] & { sql?: string, results?: any, groupKeys?: any, summaryKey?: string, error?: string, dashboardRef?: string, stackKeys?: any, stack?: boolean },
    updateFilterConfig: (path: string, index: number) => void,
    downloadAtPath: DownloadTableFunction,
    confirmAndClaimApplicant: (q: Queue) => Promise<void>,
    downloadQualifier?: string,
    publicAccessKey?: string,
    activeFilter?: BooleanExpr,
    isExploreQuery?: boolean,
};

function getCurrentUTCSuffix() {
    const currentDate = new Date();
    const year = currentDate.getUTCFullYear();
    const month = ("0" + (currentDate.getUTCMonth() + 1)).slice(-2);
    const day = ("0" + currentDate.getUTCDate()).slice(-2);
    const hours = ("0" + currentDate.getUTCHours()).slice(-2);
    const minutes = ("0" + currentDate.getUTCMinutes()).slice(-2);
    const seconds = ("0" + currentDate.getUTCSeconds()).slice(-2);
    const formattedDate = `${year}_${month}_${day}_${hours}_${minutes}_${seconds}Z`;
    return formattedDate;
}

function TableComponent(props: TableComponentProps) {
    const { component, results, sqlButton, isLoading, setIsLoading, L, localize } = props;

    const handleDownloadClick = async () => {
        setIsLoading(true);
        try {
            await props.downloadAtPath(
                component.download!.downloadPath || '',
                component.download!.filename + (props.downloadQualifier || '') + '_' + getCurrentUTCSuffix() + '.csv'
            );
        } catch (error) {
            toast.error(error + '');
        } finally {
            setIsLoading(false);
        }
    };

    if (!results && !component.download?.downloadOnly) {
        return <div>{localize(component.title)}{L.support.loading}</div>;
    }

    return <div className="mt-4 clear-both border-gray-300 border-solid border-l-4 border-r-0 border-y-0 px-4">
        {!component.download?.downloadOnly &&
            <DistroDashboardTable records={results}
                component={component}
                extra={sqlButton}
                title={localize(component.title) ?? undefined}
                count={results.length} />}
        <>
            {component.download?.downloadOnly && sqlButton}
            {component.download !== undefined &&
                <div className="clear-both">
                    <div className="bg-white inline-block p-3 rounded-xl">{component.download.filename + '.csv'}
                        {isLoading
                            ? <RigidSpinner className="mx-2" />
                            : <button className="cursor-pointer ml-2 text-gray-500 hover:text-blue-500 bg-slate-50 hover:bg-blue-200 border-solid border-2 border-blue-200 hover:border-blue-400 rounded-xl p-2"
                                onClick={handleDownloadClick}>
                                {L.dashboard.download}
                                <span className="ml-2">
                                    <DownloadIcon width="20" height="20" className="inline"/>
                                </span>
                            </button>
                        }
                    </div>
                </div>
            }
        </>
    </div>
}

export function DashboardComponent(props: DashboardComponentProps) {
    const context = useContext(InterfaceContext);
    const { activeComponent } = useContext(ActiveComponentContext);
    const [selectedIdx, setSelectedIdx] = useState(props.section.kind === 'Dashboard Section' ? props.section.activeFilter || 0 : 0);
    const L = useLocalizedStrings();
    const localize = useLocalizer();
    const [isLoading, setIsLoading] = useState(false);
    const publicConfig = useContext(PublicConfigurationContext);
    const loadDashboard = usePost('/dashboard/load');
    const [results, setResults] = useState<{
        rows: any;
        rowCount: any;
        error?: string | undefined;
    } | null>(null);

    useEffect(() => {
        // Don't try to load anything if we don't have the config or the experiment is off or the component doesn't have a token
        if (!publicConfig?.experimental?.instantLoadDashboards || !props.section?.dashboardRef) {
            return;
        }

        (async () => {
            const results = (await loadDashboard({ dashboardRef: props.section.dashboardRef, publicAccessKey: props.publicAccessKey }) as {
                rows: any;
                rowCount: any;
                error?: string | undefined;
            });
            setResults(results);
        })();
    }, [props.section.dashboardRef, publicConfig]);

    let sqlButton = <></>;
    if (props.section.sql) {
        sqlButton = <DatabaseIcon className="w-6 h-6 m-2 text-gray-400 hover:text-gray-500 cursor-pointer inline" onClick={() => {
            copyToClipboard(props.section.sql!, 'Copied SQL to clipboard');
        }} />
    }

    const localizedTitle = localize(
        ('title' in props.section && props.section.title)
            ? props.section.title
            : (('visualization' in props.section && props.section.visualization.title)
                ? props.section.visualization.title
                : ''));

    function DashboardSimpleCard(content: string) {
        const kind = props.section.kind === 'Custom Query'
            ? props.section.visualization.kind
            : props.section.kind;

        // Set some reasonable heights/widths for the placeholder cards to minimize
        // screen jumping as components load.
        let height: string, width: string;
        switch (kind) {
            case 'Bar Chart':
            case 'Custom Bar Chart':
            case 'Pie Chart':
            case 'Custom Pie Chart':
                height = '432px';
                width = '348px';
                break;
            case 'Applicant Table':
            case 'Table':
            case 'Queue':
                height = '150px';
                width = '100%';
                break;
            case 'Hero Count':
            case 'Hero Number':
            case 'Custom Number':
                height = '150px';
                width = '300px';
                break;
            default:
                height = '150px';
                width = '150px';
        }

        return (
            <div className="my-4 mr-4 float-left text-center bg-white overflow-hidden shadow rounded-lg relative" style={{height, width}}>
                <div className="px-4 py-4 sm:p-6">
                    <dl>
                        <dt className="font-semibold text-gray-500 truncate">
                            {<>{kind} - {localizedTitle}{sqlButton}</>}
                        </dt>
                        <dd className="mt-1 font-medium text-gray-400">
                            {content}
                        </dd>
                    </dl>
                </div>
            </div>
        );
    }

    // It doesn't make sense to expect there to be results if this is an explore query, or a download-only table.
    const useNewLoadStrategy = !props.isExploreQuery
        && !!publicConfig?.experimental?.instantLoadDashboards;

    // If there was an error with this component's SQL execution, surface it in a card and don't worry about what kind of component it is
    if (results?.error || props.section?.error) {
        return DashboardSimpleCard((results?.error ? results.error : props.section.error) || 'error');
    }

    // DashboardComponents are recursive in the case of Dashboard Sections,
    // so we only want to display the "loading" card for non-sections.
    // Data Dictionaries don't require any immediate querying and so have no results.
    // Tables and Custom Queries are also not permitted on public dashboards,
    // so we bypass this to remove those components entirely in the switch(kind) below.
    if (useNewLoadStrategy
            && !['Dashboard Section', 'Data Dictionary', 'Dashboard Explanation'].includes(props.section.kind)
            && !(props.section.kind === 'Applicant Table' && (props.publicAccessKey || props.section.download?.downloadOnly))
            && !(props.section.kind === 'Custom Query' && (props.publicAccessKey || (props.section.visualization.kind === 'Table' && props.section.visualization.download?.downloadOnly)))
            && !results) {
        return DashboardSimpleCard(L.support.loading);
    }

    switch (props.section.kind) {
        case 'Dashboard Section':
            let suffix: string | undefined;
            let activeFilter: BooleanExpr | undefined;
            if (props.section.filters?.length > 0) {
                suffix = '_' + props.section.filters[selectedIdx].name.replace(' ', '_').toLowerCase();
                props.section.filters[selectedIdx].filter;
            }

            return <div className="mt-4 clear-both border-gray-300 border-solid border-l-4 border-r-0 border-y-0 px-4 relative overflow-auto">
                <h2>{localize(props.section.title)}</h2>
                {props.section.filters?.length > 0 &&
                    <Dropdown description="Filter"
                        direction="right"
                    label={<>{props.section.filters[selectedIdx].name}</>}
                    options={
                        props.section.filters.map((f: { name: any; }, i: number) => {
                            return {
                                label: f.name,
                                callback: () => {
                                    if (i !== selectedIdx) {
                                        setSelectedIdx(i);
                                        props.updateFilterConfig((props.section as DashboardSection).filterPath!, i)
                                    }
                                }
                            }
                        })}
                    />}
                {/* Display semi-opaque overlay while component is loading after changing filter */}
                {(activeComponent === (props.section.filterPath ?? '') + selectedIdx) &&
                    <div className="absolute -inset-2 bg-gray-500 opacity-25 flex justify-center z-20">
                        <div className="pt-5 text-white text-6xl"><SpacedSpinner style={{ width: "3rem", height: "3rem" }}/>{' ' + L.support.loading}</div>
                    </div>
                }
                {props.section.components?.map((c: any, i: number) => {
                    return <DashboardComponent
                        key={c.kind + i}
                        section={c}
                        updateFilterConfig={props.updateFilterConfig}
                        confirmAndClaimApplicant={props.confirmAndClaimApplicant}
                        downloadAtPath={props.downloadAtPath}
                        downloadQualifier={(props.downloadQualifier || '') + (suffix || '')}
                        publicAccessKey={props.publicAccessKey}
                        activeFilter={activeFilter}
                    /> })}
            </div>
        case 'Applicant Table':
            if (props.publicAccessKey) {
                return <></>
            }
            return TableComponent({
                component: props.section,
                results: useNewLoadStrategy ? results?.rows : props.section.results,
                downloadAtPath: props.downloadAtPath,
                downloadQualifier: props.downloadQualifier,
                sqlButton: sqlButton,
                isLoading,
                setIsLoading, 
                L, 
                localize,
            });
        case 'Custom Query':
            if (props.publicAccessKey) {
                return <></>
            }
            switch (props.section.visualization.kind) {
                case 'Table':
                    return TableComponent({
                        component: props.section.visualization,
                        results: useNewLoadStrategy ? results?.rows : props.section.results,
                        downloadAtPath: props.downloadAtPath,
                        downloadQualifier: props.downloadQualifier,
                        sqlButton: sqlButton,
                        isLoading,
                        setIsLoading,
                        L,
                        localize,
                    });
                case 'Custom Number':
                    let valueToUse = props.section.results;
                    if (useNewLoadStrategy) {
                        if (results?.rowCount !== 1) {
                            // TODO: localize
                            return DashboardSimpleCard('Invalid count query. Did not return exactly one row');
                        }
                        const rowKeys = Object.keys(results.rows[0]);
                        if (rowKeys.length !== 1) {
                            // TODO: localize
                            return DashboardSimpleCard('Invalid count query. Did not return exactly one column');
                        }
                        valueToUse = results.rows[0][rowKeys[0]];
                    }
                    return <HeroNumber
                        title={<span className="flex flex-row items-center">{localizedTitle}{sqlButton}</span>}
                        value={valueToUse}
                        prefix={props.section.visualization.prefix}
                        suffix={props.section.visualization.suffix}
                    />;
                case 'Custom Bar Chart':
                    return <BarChart
                        title={<>{localizedTitle}{sqlButton}</>}
                        data={useNewLoadStrategy ? results?.rows : props.section.results}
                        groupKeys={props.section.visualization.groupKeys}
                        summaryKey={props.section.visualization.summaryKey}
                        isCustom={true}
                        hideXAxisLabels={props.section.visualization.hideXAxisLabels}
                        height={props.section.visualization.height}
                        width={props.section.visualization.width}
                        stack={props.section.visualization.stack}
                        totalKey={props.section.visualization.totalKey}
                        hideTotal={props.section.visualization.hideTotal}
                        units={props.section.visualization.units}
                    />
                case 'Custom Pie Chart':
                    return <PieChart
                        title={<>{localizedTitle}{sqlButton}</>}
                        data={useNewLoadStrategy ? results?.rows : props.section.results}
                        groupKeys={props.section.visualization.groupKeys}
                        summaryKey={props.section.visualization.summaryKey}
                        totalKey={props.section.visualization.totalKey}
                        hideTotal={props.section.visualization.hideTotal}
                        isCustom={true}
                        height={props.section.visualization.height}
                        width={props.section.visualization.width}
                        pieStyle={props.section.visualization.pieStyle || publicConfig?.interface?.defaultPieChartStyle}
                        />
            }
        case 'Dashboard Explanation':
            return <DashboardExplanationComponent explanation={props.section} />
        case 'Data Dictionary':
            return <DataDictionary component={props.section} />;
        case 'Hero Count':
            return <HeroNumber
                title={<span className="flex flex-row items-center">{localizedTitle}{sqlButton}</span>}
                value={useNewLoadStrategy ? results?.rowCount : props.section.results}
                valueToCompare={(props.section as any).originalResults}
            />;
        case 'Hero Number':
            return <HeroNumber
                title={<span className="flex flex-row items-center">{localizedTitle}{sqlButton}</span>}
                value={useNewLoadStrategy
                    ? results?.rows && results.rows.length > 0 ? results.rows[0][props.section.summaryKey ?? ''] : ''
                    : props.section.results}
                prefix={props.section.prefix}
                suffix={props.section.suffix}
            />
        case 'Bar Chart':
            let timeSeriesGroupIndex = props.section.groups.findIndex(group => group.kind === 'Bucket Date');
            return <BarChart
                title={<>{localizedTitle}{sqlButton}</>}
                data={useNewLoadStrategy ? results?.rows : props.section.results}
                groupKeys={props.section.groupKeys}
                summaryKey={props.section.summaryKey!}
                stackKeys={props.section.stackKeys}
                stack={props.section.stack}
                timeSeriesGroupIndex={timeSeriesGroupIndex}
                timeSeriesBucket={(props.section.groups[timeSeriesGroupIndex] as any)?.bucket}
                order={props.section.order}
                hideTotal={props.section.hideTotal}
                units={props.section.units}
            />
        case 'Pie Chart':
            return <PieChart 
                title={<>{localizedTitle}{sqlButton}</>} 
                data={useNewLoadStrategy ? results?.rows : props.section.results} 
                groupKeys={props.section.groupKeys} 
                summaryKey={props.section.summaryKey!} 
                height={props.section.height}
                width={props.section.width}
                pieStyle={props.section.pieStyle || publicConfig?.interface?.defaultPieChartStyle}
            />
        case 'Queue':
            if (props.publicAccessKey) {
                return <></>
            }
            return <div className="mt-4 clear-both border-gray-300 border-solid border-l-4 border-r-0 border-y-0 px-4">
                <h2>{`${L.dashboard.queue}: ${localizedTitle}`}</h2>
                <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full py-4 align-middle md:px-6 lg:px-8">
                    <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                        <table className="min-w-full divide-y divide-gray-300">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                                        {L.dashboard.number_of_applicants_in_queue}
                                    </th>
                                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                                        {L.dashboard.allowed_users}
                                    </th>
                                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                                        {L.dashboard.relevant_survey_section}
                                    </th>
                                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                                        {L.dashboard.description}
                                    </th>
                                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                                        {sqlButton}
                                    </th>
                                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6" />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                                <tr>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{useNewLoadStrategy ? results?.rowCount : props.section.numberInQueue}</td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{props.section.allowedUserTags.join(', ')}</td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{props.section.startSection}</td>
                                <td className="whiatespace-nowrap px-3 py-4 text-sm text-gray-500">{props.section.description ? props.section.description[context.lang] : ''}</td>
                                    {!props.section.hideExploreButton &&
                                        <td key='view' className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                            <a href={'/explore#' + encodeQueueForExplore(props.section, props.activeFilter)} target='_blank' className="text-indigo-600 hover:text-indigo-900">
                                                {L.dashboard.explore_queue}
                                            </a>
                                        </td>
                                    }
                                    <td key='take' className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                        <button
                                            className={`${(props.section.numberInQueue ?? 0) > 0 ? "cursor-pointer text-gray-500 hover:text-blue-500 bg-slate-50 hover:bg-blue-200 border-solid border-2 border-blue-200 hover:border-blue-400" : "border-none"} ml-2 rounded-xl p-2`}
                                            disabled={props.section.numberInQueue === 0}
                                            onClick={async () => props.confirmAndClaimApplicant(props.section as Queue)}>
                                            {L.applicant.claim_applicant}
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
        default:
            return <p>{`${props.section.kind} ${L.dashboard.not_supported_yet}`}</p>
    }
}

const ActiveComponentContext = createContext({ activeComponent: null as string | null });

export function DistroDashboard(props: { dashboard?: string, isEmbedded?: boolean }): JSX.Element {
    const params = new URLSearchParams(window.location.search);
    const publicAccessKey = params.get("key") ?? undefined;
    const config: PublicConfig = useContext(PublicConfigurationContext);
    const { path } = useParams() as Record<string, string>
    const runQuery = usePost('/dashboard/view');
    const getDashboard = usePost('/dashboard/getDashboards');
    const downloadCSV = usePost('/dashboard/download', { textOnly: true });
    const takeApplicant = usePost('/queues/take_applicant');
    const L = useLocalizedStrings();
    const localize = useLocalizer();
    const history = useHistory();
    const [results, setResults] = useState<survey.Dashboard | null>(null);
    const [filterConfig, setFilterConfig] = useState<Record<string, number>>({});
    const [loadedPath, setLoadedPath] = useState<string | null>(null);
    // This is passed to children to let them know whether to display the "Loading..." overlay
    const [activeComponent, setActiveComponent] = useState<string | null>(null);
    // This is used by the main dashboard to indicate if it is currently executing any queries
    const [isRunning, setIsRunning] = useState<boolean>(false);

    const context = useContext(PublicConfigurationContext);
    const appPath = survey.interfaceNumber(context?.interface?.version) >= 1 ? '/a/' : '/applicant/';

    useEffect(() => {
        // Don't try to load anything until the config has been loaded so we know which method to use.
        // Object.keys check because the config can be {} before it is loaded
        if (!config || Object.keys(config).length === 0) {
            return;
        }

        setIsRunning(true);
        (async () => {
            // TODO: this reruns every query on the dashboard anytime the dashboard rerenders.
            // We should find a better way to do this
            const results = (config?.experimental?.instantLoadDashboards)
                ? await getDashboard({ dashboard: props.dashboard ?? path, filterIndexes: filterConfig, publicAccessKey })
                : await runQuery({ dashboard: props.dashboard ?? path, filterIndexes: filterConfig, key: publicAccessKey });

            setResults(results as any);
            setLoadedPath(path);
            setActiveComponent(null);
            setIsRunning(false);
        })();
    }, [filterConfig, path, config])

    async function downloadAtPath(downloadPath: string, downloadName: string) {
        const data = await downloadCSV({dashboard: path, filterIndexes: filterConfig, downloadPath });
        const blob = new Blob([data as any], {type: 'text/csv'});
        const elem = window.document.createElement('a');
        elem.href = window.URL.createObjectURL(blob);
        elem.download = downloadName;
        document.body.appendChild(elem);
        elem.click();
        document.body.removeChild(elem);
    }

    async function confirmAndClaimApplicant(queue: Queue): Promise<void> {
        if (window.confirm(L.dashboard.claim_applicant_confirmation)) {
            // This is required for safari to not block the popup in an async handler
            let win = window.open();

            let resp: any;
            try {
                resp = await takeApplicant({ queue: queue });
            } catch (e) {
                console.warn("Error: ", e)
                toast.error(L.dashboard.claim_applicant_error);
                win?.close();
                return;
            }
            console.log(resp);
            if (resp.applicant) {
                const loc = appPath + resp.applicant + (queue.startSection ? '#' + encodeURIComponent(queue.startSection) : '');
                if (win) {
                    win.location = loc;
                    win.focus();
                } else {
                    history.push(loc)
                }
                toast.success(L.dashboard.applicant_claimed);
            } else {
                toast.error(L.dashboard.claim_applicant_error);
                win?.close();
            }
        }
    }

    return (props.isEmbedded || publicAccessKey)
        ? <div className="h-full p-4 px-8">
            {results && results.components &&
                <>
                    <div className="inline-block">
                        <h1 className="inline">{localize(results.title)}</h1>
                        {isRunning && <SpacedSpinner className="ml-3 inline" style={{ width: "2rem", height: "2rem" }} />}
                    </div>
                    <ActiveComponentContext.Provider value={{activeComponent}}>
                        {results.components.map((s, i) => {
                            return <DashboardComponent
                                key={s.kind + i}
                                section={s}
                                updateFilterConfig={(path: string, idx: number) => {
                                    setActiveComponent(path + idx);
                                    setFilterConfig((prev) => ({...prev, [path]: idx}));
                                }}
                                confirmAndClaimApplicant={confirmAndClaimApplicant}
                                downloadAtPath={downloadAtPath}
                                publicAccessKey={publicAccessKey}
                            />
                        })}
                    </ActiveComponentContext.Provider>
                </>}
            </div>
        : <ThreeColumnPage main={
            <div className="bg-gray-100 p-4 px-8 mb-3">
                {path === loadedPath && results && results.components &&
                    <>
                        <div className="inline-block">
                            <h1 className="inline">{localize(results.title)}</h1>
                            {isRunning && <SpacedSpinner className="ml-3 inline" style={{ width: "2rem", height: "2rem" }} />}
                        </div>
                        <ActiveComponentContext.Provider value={{activeComponent}}>
                            {results.components.map((s, i) => {
                                return <DashboardComponent
                                    key={s.kind + i}
                                    section={s}
                                    updateFilterConfig={(path: string, idx: number) => {
                                        setActiveComponent(path + idx);
                                        setFilterConfig((prev) => ({...prev, [path]: idx}));
                                    }}
                                    confirmAndClaimApplicant={confirmAndClaimApplicant}
                                    downloadAtPath={downloadAtPath}
                                />
                            })}
                        </ActiveComponentContext.Provider>
                    </>}
                {(!results || path !== loadedPath) && <><SpacedSpinner /> {L.dashboard.loading_message}</>}
            </div>}
        />;
}
